import React from "react";
import { Pencil, Trash2, Archive, Crown, ShieldCheck, Monitor, User as UserIcon } from "lucide-react";
import { Badge, Avatar } from "../../components/primitives";
import { CountPill, IconBtn } from "../../components/RowControls";
import { Toggle } from "../../components/Toggle";
import {
  displayMemberEmail, resolveReportingManagers, formatManagerName, memberName,
  roleLabel, permissionCount, permissionFields,
} from "./teamHelpers";

const statusTone = (s) => (s === "archived" ? "neutral" : s === "inactive" ? "neutral" : s === "pending" ? "warning" : "positive");

// Column definitions for the team list. Kept out of the screen file so the screen
// stays about data and state.
export function teamColumns({
  t, roles, allUsers, isAdmin, currentUserId, showCrm,
  onInfo, onEdit, onDelete, onArchive, onStealth, onPermissions,
}) {
  const isSelf = (u) => (u.id && u.id === currentUserId) || (u._id && u._id === currentUserId);

  return [
    {
      key: "name", header: "Member",
      render: (u) => {
        const activated = u.isActivated;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, maxWidth: 300 }}>
            <span style={{ position: "relative", flexShrink: 0, display: "inline-flex" }}>
              <Avatar user={u} name={memberName(u)} size={30} />
              {/* Activation state as a dot on the avatar — it used to be a badge
                  competing with five others. */}
              {activated != null && (
                <span title={activated ? "Activated" : "Not activated"} style={{
                  position: "absolute", bottom: -1, right: -1, width: 9, height: 9, borderRadius: 99,
                  background: activated ? t.status.positive : t.c.textTertiary,
                  border: `2px solid ${t.c.surface}`,
                }} />
              )}
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ fontSize: t.font.size.md, fontWeight: 500, color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{memberName(u)}</span>
                {isSelf(u) && <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, flexShrink: 0 }}>(you)</span>}
              </div>
              <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {displayMemberEmail(u)}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: "role", header: "Role", width: 120,
      render: (u) => {
        const orgAdmin = u.role === "org_admin";
        return <Badge tone={orgAdmin ? "warning" : "brand"}>{orgAdmin && <Crown size={11} />}{roleLabel(roles, u.role)}</Badge>;
      },
    },
    {
      key: "position", header: "Position", width: 170,
      render: (u) => (
        <div style={{ minWidth: 0, maxWidth: 170 }}>
          <div style={{ fontSize: t.font.size.sm, color: u.position ? t.c.text : t.c.textTertiary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {u.position || "—"}
          </div>
          {u.department && (
            <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.department}</div>
          )}
        </div>
      ),
    },
    {
      key: "managers", header: "Reports to", width: 140,
      render: (u) => {
        const mgrs = resolveReportingManagers(u, allUsers);
        if (mgrs.length === 0) return <span style={{ color: t.c.textTertiary }}>—</span>;
        return (
          <span title={mgrs.map(formatManagerName).join(", ")} style={{ display: "inline-flex", alignItems: "center" }}>
            {mgrs.slice(0, 3).map((m, i) => (
              <span key={m._id || m.id || i} style={{ marginLeft: i ? -8 : 0, border: `2px solid ${t.c.surface}`, borderRadius: 99, display: "inline-flex" }}>
                <Avatar user={m} name={formatManagerName(m)} size={24} />
              </span>
            ))}
            {mgrs.length > 3 && (
              <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginLeft: 4 }}>+{mgrs.length - 3}</span>
            )}
          </span>
        );
      },
    },
    {
      key: "status", header: "Status", width: 104,
      render: (u) => {
        const s = u.status || "active";
        return <Badge tone={statusTone(s)} dot>{s}</Badge>;
      },
    },
    {
      key: "agent", header: "Agent", width: 116,
      render: (u) => {
        if (u.hasDesktopAgent) {
          return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: t.font.size.sm, color: t.c.text, whiteSpace: "nowrap" }}>
              <Monitor size={13} style={{ color: t.status.positive }} /> Yes
            </span>
          );
        }
        if (u.latestAppVersion) {
          return <span style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, whiteSpace: "nowrap" }}>v{u.latestAppVersion}</span>;
        }
        return <span style={{ color: t.c.textTertiary }}>—</span>;
      },
    },
    {
      key: "permissions", header: "Access", align: "right", width: 96,
      render: (u) => {
        const total = permissionFields(showCrm).length;
        const n = permissionCount(u, showCrm);
        return (
          <CountPill
            icon={<ShieldCheck size={13} />}
            value={`${n}/${total}`}
            muted={n === 0}
            title={`Permissions and reporting for ${memberName(u)}`}
            onClick={() => onPermissions(u)}
          />
        );
      },
    },
    {
      key: "stealth", header: "Stealth", align: "center", width: 88,
      render: (u) => (
        <Toggle on={!!u.stealth_mode} onClick={(e) => { e.stopPropagation(); onStealth(u); }} />
      ),
    },
    {
      key: "actions", header: "", align: "right", width: 128,
      render: (u) => (
        <div className="tg-row-actions" style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
          <IconBtn title="View profile" onClick={() => onInfo(u)}><UserIcon size={15} /></IconBtn>
          <IconBtn title="Edit" onClick={() => onEdit(u)}><Pencil size={15} /></IconBtn>
          {/* Archiving yourself would lock you out of your own org. */}
          {isAdmin && !isSelf(u) && u.status !== "archived" && (
            <IconBtn title="Archive" onClick={() => onArchive(u)}><Archive size={15} /></IconBtn>
          )}
          {!isSelf(u) && <IconBtn title="Remove" danger onClick={() => onDelete(u)}><Trash2 size={15} /></IconBtn>}
        </div>
      ),
    },
  ];
}
